import { HttpErrorResponse } from '@angular/common/http';

/**
 * Structured error message for toast notifications.
 */
export interface ErrorToast {
  title: string;
  description?: string;
}

/** Last-resort title so a toast never renders blank. */
export const DEFAULT_ERROR_TITLE = 'Something went wrong';

/**
 * Extracts a structured error toast from a backend API response.
 *
 * Defensive on purpose: the backend's `message` is usually a structured
 * `{ title, description }` object, so we must never hand that object back as a
 * title (which the toast template would render as "[object Object]"). The
 * returned `title` is ALWAYS a non-empty string — a blank title falls back to
 * the optional `fallback`, then to DEFAULT_ERROR_TITLE.
 *
 * ── Ownership convention ──────────────────────────────────────────────────
 * Call this function WITHOUT a fallback string on every API-triggered error:
 *   ✅  extractApiError(err)
 *   ❌  extractApiError(err, 'Hardcoded fallback')
 *
 * Omitting the fallback signals that the backend owns the error copy.
 * The only approved categories of frontend-owned error text are:
 *   • Pre-flight client-side validation (fired before the HTTP call)
 *   • The 403 interceptor (no response body available)
 *   • Clipboard / local utility failures (no API involved)
 * ─────────────────────────────────────────────────────────────────────────
 */
export function extractApiError(
  err: HttpErrorResponse,
  fallback = ''
): ErrorToast {
  const msg = err?.error?.message;
  if (msg && typeof msg === 'object') {
    const record = msg as { title?: unknown; description?: unknown };
    const rawTitle =
      typeof record.title === 'string' && record.title ? record.title : fallback;
    const description =
      typeof record.description === 'string' ? record.description : undefined;
    return { title: rawTitle || DEFAULT_ERROR_TITLE, description };
  }
  if (typeof msg === 'string' && msg) {
    return { title: msg };
  }
  return { title: fallback || DEFAULT_ERROR_TITLE };
}
